import { DestroyRef, Injectable, OnDestroy, inject } from '@angular/core';
import { IUserAttributes, IUserProfile, IUserProfileLoader } from 'ngx-register-base';
import { KeycloakEventType, KeycloakOptions, KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, filter, firstValueFrom, from } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IClsMenuItem } from '../components/nav-menu/store/nav-menu-schema';
import { SmaPageMenuStore } from '../components/nav-menu/store/nav-menu-store';
import { SentryService } from './sentry.service';
import { ERoles } from '../shared/types/user-roles';

@Injectable({
  providedIn: 'root',
})
export class UserProfileService implements IUserProfileLoader<IUserProfile, any>, OnDestroy {
  private readonly keycloakService = inject(KeycloakService);
  private readonly _dr = inject(DestroyRef);
  private readonly _nav = inject(SmaPageMenuStore);

  private readonly _isLoggedIn$ = new BehaviorSubject(false);
  private readonly _token$ = new BehaviorSubject('');
  private readonly _userProfile$ = new BehaviorSubject<IUserProfile | undefined>(undefined);

  public mainMenu: IClsMenuItem[] = [];
  private mainMenuCodes: string[] = [];

  protected sentry = inject(SentryService);

  constructor() {
    from(this.keycloakService.keycloakEvents$)
      .pipe(
        filter((event) => event.type === KeycloakEventType.OnTokenExpired),
        takeUntilDestroyed(this._dr)
      )
      .subscribe(() => {
        this._updateTokenHandler();
      });
  }

  public async initKeycloak(initOptions: KeycloakOptions): Promise<void> {
    try {
      const initiated = await this.keycloakService.init(initOptions);
      const response = await firstValueFrom(this._nav.fetchMenu());
      this.mainMenu = response?.data?.usr_cls_menu_item ?? [];
      this.mainMenuCodes = this.mainMenu.map((item) => item.code);

      if (initiated) {
        const userProfile = await this._loadUserProfile();
        this._userProfile$.next(userProfile);
        this.sentry.setUserContext(userProfile.username);
        this._isLoggedIn$.next(true);
        this.keycloakService.getKeycloakInstance().onTokenExpired = this._updateTokenHandler;
      }
    } catch (error) {
      console.error(error);
    }
  }

  public getUserProfile(): IUserProfile {
    return this._userProfile$.value!;
  }

  public getMenuItemByRoute(route: string): IClsMenuItem | undefined {
    return this.mainMenu.find((item) => item.route === route);
  }

  public checkPermissions(permissions: any[], every?: boolean): boolean {
    const userPermissions = this.getUserProfile()?.permissions ?? [];
    if (every) {
      return permissions.every((permission) => userPermissions.includes(permission));
    }

    return permissions.some((permission) => userPermissions.includes(permission));
  }

  public checkWritePermission(writeCode: string): boolean {
    const userPermissions = this.getUserProfile()?.permissions ?? [];
    return userPermissions.includes(`write_${writeCode}`);
  }

  public canWriteReference(referenceCode: string): boolean {
    return this.checkRoles([ERoles.ADMIN]) || this.checkWritePermission(referenceCode);
  }

  public checkRoles(roles: any[], every?: boolean): boolean {
    const userRoles = this.getUserProfile()?.roles ?? [];
    if (every) {
      return roles.every((permission) => userRoles.includes(permission));
    }

    return roles.some((permission) => userRoles.includes(permission));
  }

  public hasMenuPath(path: string): boolean {
    const paths = new Set(this.mainMenuCodes);
    return paths.has(path);
  }

  public logout(): void {
    this.keycloakService
      .logout()
      .then(() => {
        this._token$.next('');
        this._userProfile$.next({ id: '', token: '', username: '' });
      })
      .catch((error) => {
        console.error(error);
      });
    this.sentry.clearUserContext();
  }

  public get token(): string {
    return this._token$.getValue();
  }

  public ngOnDestroy(): void {
    this.logout();
  }

  private async _loadUserProfile(): Promise<IUserProfile> {
    const [profile, userInfo, token] = await Promise.all([
      this.keycloakService.loadUserProfile(),
      this.keycloakService.getKeycloakInstance().loadUserInfo(),
      this.keycloakService.getToken(),
    ]);

    const keycloakInstance = this.keycloakService.getKeycloakInstance();
    const roles: string[] = keycloakInstance.realmAccess ? keycloakInstance.realmAccess.roles : [];
    this._token$.next(token);

    let picture = '';
    const userAttributes: IUserAttributes[] = [];
    if (profile.attributes) {
      for (const [key, value] of Object.entries(profile.attributes)) {
        if (key === 'picture') {
          picture = value as string;
        }
        const attribute: IUserAttributes = {
          name: key,
          value: value as string,
        };
        userAttributes.push(attribute);
      }
    }

    const usr = {
      id: (userInfo as any).sub,
      firstName: profile.firstName,
      lastName: profile.lastName,
      username: profile.username,
      roles,
      // группы - массив id назначенных групп (id_subsidiary). Название поля соответствует маппингу в клиенте кейклоак
      groups:
        ((userInfo as any).hasura_jwt_claims &&
          (userInfo as any).hasura_jwt_claims['x-hasura-groups']) ??
        [],
      groups_name: (userInfo as any).groups,
      token,
      permissions: (userInfo as any)['allowed-permissions'],
      attributes: userAttributes,
      photo: picture,
    };
    return usr;
  }

  private _updateTokenHandler: () => void = () => {
    this.keycloakService
      .updateToken(-1)
      .then(() => this._setTokenToSubjectFromKeycloak())
      .catch(() => this.logout());
  };

  private _setTokenToSubjectFromKeycloak(): void {
    const { token } = this.keycloakService.getKeycloakInstance();

    if (token) {
      this._token$.next(token);
    }
  }

  public inGroup(departmentCode: string): boolean {
    return this._userProfile$.value?.groups?.includes(departmentCode) ?? false;
  }
}
